<script setup lang="ts">
import { api } from '@/api';
import { type UserDto, UserDtoTypeEnum, UserStateOfUse } from '@/api/generator';
import PtwBackButton from '@/components/buttons/PtwBackButton.vue';
import SubmitButton from '@/components/forms/SubmitButton/SubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { isActionEnabled } from '@/utils/actions';
import { fullName } from '@/utils/helper';
import { useLoading } from '@/utils/useLoading';
import { useSendActivationLink } from '@/views/accounts/utils.ts';
import { useTranslation } from 'i18next-vue';
import { useSetFormValues } from 'vee-validate';
import { computed, onMounted, ref } from 'vue';
import AccountForm from './components/AccountForm.vue';
import type { AccountDetailsViewProps } from './types';
import { entityNotFoundErrorHandler } from '@/errorHandling';
import { useRouter } from 'vue-router';

const { t } = useTranslation();

const props = defineProps<AccountDetailsViewProps>();
const isDisabledAfterSent = ref(false);

const setFormValues = useSetFormValues();
const { sendActivationLink, loading: activationLoading } = useSendActivationLink();

const router = useRouter();

const {
  form: { resetForm, values },
} = useVeeForm<UserDto>({
  mode: 'details',
});

const { action: loadAccount, loading } = useLoading(async () => {
  const { result } = await api.usersApi.getUser(
    {
      id: props.id,
    },
    [entityNotFoundErrorHandler(router)],
  );

  if (result) {
    resetForm({
      values: {
        ...result,
        $type:
          UserDtoTypeEnum.PtwPtw100AppZeroApplicationSharedAuthorizationUsersDtoUserDtoPtwPtw100AppZeroApplicationShared,
      },
    });
  }
}, true);

const sendActivation = async () => {
  const inviteResult = await sendActivationLink(values);

  if (inviteResult.success && inviteResult.result) {
    isDisabledAfterSent.value = true;

    setFormValues({
      lastModificationTime: inviteResult.result.lastModificationTime,
    });
  }
};

// This extended check will be replaced by use of possibleAction from backend
const canInviteUser = computed(
  () =>
    isActionEnabled(values, 'SendActivation') &&
    values.state &&
    (values.state === UserStateOfUse.NoActivationSent ||
      values.state === UserStateOfUse.ActivationPending),
);

const userStateDependentActivationMessage = computed(() => {
  if (values.state === UserStateOfUse.NoActivationSent)
    return {
      buttonText: t('SendActivationLink'),
      msgText: t('NoActivationLinkSentYetMsg'),
    };
  if (UserStateOfUse.ActivationPending === values.state)
    return {
      buttonText: t('ResendActivationLink'),
      msgText: t('ActivationLinkSentButNoActivatedYetResendMsg'),
    };
  return undefined;
});

onMounted(loadAccount);
</script>
<template>
  <BasePage :loading="loading">
    <template #title>
      <BasePageTitle
        :subtitle="$t('UserAccount_one')"
        :title="fullName(values)"
        :titleId="values.iuid"
      />
    </template>

    <template #status>
      <BasePageStatus :entity="values" />
    </template>

    <template #actions>
      <PtwBackButton :to="{ name: routenames.accounts.overview }" />
    </template>

    <AccountForm>
      <template v-if="userStateDependentActivationMessage" #activationAction>
        <p class="text-body-2 font-weight-medium mb-2 text-primary">
          {{ userStateDependentActivationMessage?.msgText }}
        </p>
        <SubmitButton
          v-if="canInviteUser"
          size="default"
          :loading="activationLoading"
          :disabled="isDisabledAfterSent"
          :text="userStateDependentActivationMessage?.buttonText"
          @click="sendActivation"
        >
        </SubmitButton>
        <VDivider class="mt-5"></VDivider>
      </template>
    </AccountForm>
  </BasePage>
</template>
